// In Next.js, this file would be called: app/providers.tsx
"use client";

// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { useEffect } from "react";
import {
  isServer,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import {
  clearServerActionSkewReloadGuard,
  isServerActionSkewError,
  recoverFromServerActionSkew,
} from "@/lib/http-error-status";

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        recoverFromServerActionSkew(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        recoverFromServerActionSkew(error);
      },
    }),
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 5 * 60 * 1000, // 5 minutes instead of Infinity
        retry: (failureCount, error) => {
          if (isServerActionSkewError(error)) {
            return false;
          }
          // Don't retry on 404 errors (resource not found)
          if (
            error &&
            typeof error === "object" &&
            "status" in error &&
            error.status === 404
          ) {
            return false;
          }
          // Don't retry on 403 errors (forbidden) - these should trigger error boundary
          if (
            error &&
            typeof error === "object" &&
            "status" in error &&
            error.status === 403
          ) {
            return false;
          }
          // Retry up to 3 times for other errors
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Note: Global onError handlers don't throw to error boundaries
        // Route-level 403 errors are handled by RouteOrgValidator component
        // which throws during render to trigger the error boundary
      },
      mutations: {
        // Global error handler for mutations
        // Mutations can show toasts, but route-level validation mutations should throw
        onError: (error) => {
          recoverFromServerActionSkew(error);
        },
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

function ServerActionSkewRecovery() {
  useEffect(() => {
    clearServerActionSkewReloadGuard();

    const onUnhandled = (event: PromiseRejectionEvent) => {
      if (recoverFromServerActionSkew(event.reason)) {
        event.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandled);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandled);
    };
  }, []);

  return null;
}

export default function ReactQueryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // NOTE: Avoid useState when initializing the query client if we don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ServerActionSkewRecovery />
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
